import {
  LogOut,
  UserRound,
} from "lucide-react"

import { UserAvatar } from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  sidebarCollapseChevronClassName,
  sidebarCollapseLabelClassName,
  useSidebar,
} from "@/components/ui/sidebar"
import {
  SidebarChevronIcon,
  SidebarSettingsIcon,
} from "@/components/sidebar/sidebar-icons"
import { cn } from "@/lib/utils"
import { useTranslation } from "react-i18next"
import { useAuth } from "@/lib/auth"
import { useNavigate } from "react-router"
import { ThemeSwitcher } from "./ThemeSwitcher"
import { LanguageSwitcher } from "./LanguageSwitcher"

type NavUserProps = {
  user: {
    name: string | null
    email: string
    avatar: string | null
  }
  variant?: 'default' | 'mobileFooter'
}

export function NavUser({ user, variant = 'default' }: NavUserProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { isMobile } = useSidebar()
  const { logout, user: authUser } = useAuth({
    middleware: 'auth',
    redirectTo: '/',
    tmp: 'NavUser',
  })
  const isImpersonating = Boolean(authUser?.impersonatorId)
  const displayName = user.name || user.email
  const fullName = authUser?.party?.firstName ?? displayName

  const menu = (
    <DropdownMenuContent
      className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
      side={isMobile ? "bottom" : "right"}
      align="end"
      sideOffset={4}
    >
      <DropdownMenuLabel className="p-0 font-normal">
        <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
          <UserAvatar
            className="h-8 w-8 rounded-lg"
            photoSrc={user.avatar}
            alt={displayName}
            fallbackClassName="rounded-lg"
            fallback={displayName?.charAt(0).toUpperCase()}
          />
          <div className="grid flex-1 text-left text-sm leading-tight">
            <span className="truncate font-medium">{user.name}</span>
            <span className="truncate text-xs">{user.email}</span>
          </div>
        </div>
      </DropdownMenuLabel>
      <LanguageSwitcher className="my-2" />
      <ThemeSwitcher className="my-2" />
      <DropdownMenuSeparator />
      <DropdownMenuGroup>
        <DropdownMenuItem onClick={() => navigate('/settings')}>
          <SidebarSettingsIcon className="size-4" />
          {t('settings.title')}
        </DropdownMenuItem>
      </DropdownMenuGroup>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={logout}>
        <LogOut />
        {t(isImpersonating ? 'sidebar.endImpersonation' : 'sidebar.logout')}
      </DropdownMenuItem>
    </DropdownMenuContent>
  )

  if (variant === 'mobileFooter') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center gap-3 rounded-md border border-white/50 bg-transparent px-2.5 py-2 text-left text-white hover:bg-white/10"
          >
            <UserAvatar
              className="size-[34px] rounded-md bg-surface-alt"
              photoSrc={user.avatar}
              alt={displayName}
              fallbackClassName="rounded-md bg-surface-alt text-brand-secondary"
              fallback={
                displayName?.charAt(0).toUpperCase() || (
                  <UserRound className="size-4 text-brand-secondary" />
                )
              }
            />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-body font-medium text-white">
                {fullName}
              </span>
              <span className="mt-0.5 block truncate font-label text-white/80">
                {user.email}
              </span>
            </span>
            <SidebarChevronIcon className="opacity-80" />
          </button>
        </DropdownMenuTrigger>
        {menu}
      </DropdownMenu>
    )
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className={cn(
                "h-[35px] rounded-md border border-white/50 bg-transparent px-2 py-0",
                "hover:bg-white/10 hover:text-white",
                "data-[state=open]:bg-white/10 data-[state=open]:text-white",
                "group-data-[collapsible=icon]:size-[35px]! group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:border-white/50 group-data-[collapsible=icon]:p-0!",
              )}
              tooltip={
                [user.name, user.email].filter(Boolean).join(" · ") || undefined
              }
            >
              <UserAvatar
                className="size-[22px] rounded-full bg-surface-alt"
                photoSrc={user.avatar}
                alt={displayName}
                fallbackClassName="rounded-full bg-surface-alt text-brand-secondary"
                fallback={
                  displayName?.charAt(0).toUpperCase() || (
                    <UserRound className="size-3.5 text-brand-secondary" />
                  )
                }
              />
              <span
                className={cn(
                  "min-w-0 flex-1 truncate text-left text-xs text-white",
                  sidebarCollapseLabelClassName,
                )}
              >
                {displayName}
              </span>
              <SidebarChevronIcon
                className={cn(sidebarCollapseChevronClassName, "opacity-80")}
              />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          {menu}
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
